import React from 'react';
import { Search, BookOpen, MessageCircle, Video } from 'lucide-react';

/**
 * WelcomeSection - Home page welcome area with stats and search
 */
function WelcomeSection({ 
  dynamicStats, 
  searchTerm, 
  onSearchChange, 
  searchResults,
  onCourseSelect,
  specializations,
  userName
}) {
  // Greeting based on time of day
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'בוקר טוב' : hour < 18 ? 'צהריים טובים' : 'ערב טוב';
  const displayName = userName || '';

  return (
    <>
      {/* Welcome Header */}
      <div className="text-center py-6">
        {displayName && (
          <p className="text-slate-400 text-base mb-1" data-testid="user-greeting">
            {greeting}, <span className="text-teal-500 font-semibold">{displayName}</span>
          </p>
        )}
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" data-testid="hero-title">
          <img 
            src="/logo.png" 
            alt="הצ'ייסר" 
            className="h-12 sm:h-14 mx-auto object-contain select-none pointer-events-none"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </h1>
        <p className="text-slate-500 text-base mb-6">פלטפורמת הלמידה החכמה שלך</p>
        
        {/* Statistics - Dynamic with icons */}
        <div className="flex justify-center gap-6 sm:gap-10 mb-4">
          <div className="text-center group cursor-default" data-testid="stat-courses">
            <div className="flex items-center justify-center gap-1 mb-1">
              <BookOpen className="w-4 h-4 text-teal-400 opacity-60" />
              <div className="text-2xl sm:text-3xl font-black text-teal-500">{dynamicStats?.total_courses || 244}</div>
            </div>
            <div className="text-xs sm:text-sm text-slate-500">קורסים</div>
          </div>
          <div className="text-center group cursor-default" data-testid="stat-questions">
            <div className="flex items-center justify-center gap-1 mb-1">
              <MessageCircle className="w-4 h-4 text-teal-400 opacity-60" />
              <div className="text-2xl sm:text-3xl font-black text-teal-500">
                {dynamicStats?.total_questions ? `${(dynamicStats.total_questions / 1000).toFixed(1)}K+` : '85K+'}
              </div>
            </div>
            <div className="text-xs sm:text-sm text-slate-500">שאלות</div>
          </div>
          <div className="text-center group cursor-default" data-testid="stat-videos">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Video className="w-4 h-4 text-teal-400 opacity-60" />
              <div className="text-2xl sm:text-3xl font-black text-teal-500">+{dynamicStats?.total_videos || 498}</div>
            </div>
            <div className="text-xs sm:text-sm text-slate-500">סרטונים</div>
          </div>
        </div>
      </div>

      {/* Dynamic Course Search */}
      <div className="bg-white p-5 rounded-2xl shadow-md border border-slate-100 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="חפש קורס... (התחל להקליד)"
            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 outline-none pr-12 text-base font-medium transition-all"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="global-course-search"
          />
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
        </div>
        
        {/* Dynamic Search Results */}
        {searchTerm.length > 0 && searchResults && (
          <div className="mt-3 space-y-1 max-h-80 overflow-y-auto">
            {searchResults.length > 0 ? (
              searchResults.map((course) => (
                <button
                  key={course.id}
                  onClick={() => onCourseSelect(course)}
                  className="w-full text-right p-3 rounded-xl hover:bg-teal-50 border border-transparent hover:border-teal-200 transition-all flex justify-between items-center group"
                  data-testid={`search-result-${course.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg opacity-60 group-hover:opacity-100 transition-opacity">
                      {course.specIcon || '📚'}
                    </span>
                    <div>
                      <div className="font-bold text-slate-900 text-sm">{course.name}</div>
                      <div className="text-xs text-slate-500">
                        {course.specName} • שנה {course.year}
                        {course.question_count > 0 && ` • ${course.question_count} שאלות`}
                      </div>
                    </div>
                  </div>
                  <span className="text-teal-500 font-bold text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    לקורס ←
                  </span>
                </button>
              ))
            ) : (
              <div className="text-center py-6 text-slate-400">
                <p className="text-sm">לא נמצאו קורסים עבור "{searchTerm}"</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default WelcomeSection;
